/**
 * One keystroke → ONE `palette_search` (integration of A + B, PLAN §3.3).
 *
 * Every index-backed provider (tasks, boards, documents, calendar, …) asks
 * the index for its own kinds. One call each meant ~6 queries per keystroke,
 * serialized on the account database's single lock — p95 170 ms at 100k
 * tasks + 100k documents against a budget of 80 ms. The palette fires the
 * providers that share a debounce in the same task (`runSearch`), so this
 * wrapper collects the calls made in that task (until the microtask
 * checkpoint), merges those with the same query and `body` into one call
 * over the union of kinds and scopes with `limitPerKind` (Rust: `limit` and
 * the candidate window per kind, so every provider gets what its own call
 * would have returned), and hands each provider its kinds in its scopes,
 * best first. Merging scopes is safe: record kinds live in record scopes
 * (`personal:` / `team:` / `device:`), requests and collections in API
 * workspace ids, and each caller's hits are filtered by its own scopes too.
 *
 * A lone call goes through unchanged. Each caller keeps its own abort: an
 * aborted caller gets an `AbortError`; the shared call is skipped only when
 * every caller in its group aborted before it was sent. Errors reach every
 * caller of that group. `get` / `status` / `rebuild` and the events pass
 * through.
 */
import { abortError } from './abort';

const groupKey = (args) => JSON.stringify([String(args.q ?? ''), !!args.body]);

/**
 * @param {object} client  §12.2 index client (`search(args, { signal })`, …)
 * @param {{ schedule?: (fn: () => void) => void }} [opts]
 */
export function createBatchingClient(client, { schedule = (fn) => queueMicrotask(fn) } = {}) {
  let pending = [];

  function send(group) {
    const live = group.filter((p) => !p.signal?.aborted);
    for (const p of group) if (p.signal?.aborted) p.reject(abortError());
    if (!live.length) return;
    if (live.length === 1) {
      const [p] = live;
      client.search(p.args, { signal: p.signal }).then(p.resolve, p.reject);
      return;
    }
    const kinds = [...new Set(live.flatMap((p) => p.args.kinds || []))];
    const limit = Math.max(...live.map((p) => p.args.limit ?? 50));
    const scopes = [...new Set(live.flatMap((p) => (p.args.scopes || []).map(String)))];
    const { q, body } = live[0].args;
    client.search({ q, kinds, scopes, limit, body: !!body, limitPerKind: true }).then(
      (res) => {
        for (const p of live) {
          if (p.signal?.aborted) {
            p.reject(abortError());
            continue;
          }
          const mine = new Set(p.args.kinds || []);
          const where = new Set((p.args.scopes || []).map(String));
          const own = (res?.hits || []).filter((h) => mine.has(h.kind) && where.has(String(h.scope))).slice(0, p.args.limit ?? 50);
          p.resolve({ ...res, hits: own });
        }
      },
      (err) => live.forEach((p) => p.reject(err))
    );
  }

  function flush() {
    const list = pending;
    pending = [];
    const groups = new Map();
    for (const p of list) {
      const k = groupKey(p.args);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(p);
    }
    groups.forEach(send);
  }

  return {
    ...client,
    search(args = {}, { signal } = {}) {
      if (signal?.aborted) return Promise.reject(abortError());
      return new Promise((resolve, reject) => {
        if (!pending.length) schedule(flush);
        pending.push({ args, signal, resolve, reject });
      });
    },
  };
}
